import createModule from "../createModule"
import view from "./view"
import { Creators as ProfileCreators } from "model/profileModal"
import { Creators as AnalyzeConcentrationDetailsCreators } from "modules/analyze-concentration-details/model"
import { Creators as CompositeCreators } from "modules/analyze-composite-security/model"
import { getSelectedModelId, getIsExpanded } from "modules/portfolio-root/selectors/portfolios"
import { getEffectiveSegment } from "modules/analyze-header/selectors/effective-segment"
import { generateClassificationQueryForContributors, getClassifications } from "helpers/classification"
import { getQueryGroupBy } from "helpers/rollups"
import { getBaseSignature, getBasePositionSetWithSegment } from "selectors/base"
import { getIsOpPro } from "modules/account-root/selectors"
import { get } from "lodash"
import { sanitizeBasketTicker } from "helpers/swaps"
import {
  getSortedContributors,
  getSortedGroupContributors,
  getSelectedGroup,
  getSelectedGroupValue,
  getAvailableRollups,
  getContributorsSummaryText,
  getCsvData,
  hasReferenceEquity,
  getSelectedBasket,
  getSelectedGroupSiblings,
} from "./selectors"
import { getGroupsDropdownColumns } from "./selectors/group"

export default createModule({
  view,
  externalProps: (state, props) => ({
    account: state.global.selected.account,
    portfolioId: state.global.selected.portfolio,
    modelId: getSelectedModelId(state, props),
    segment: getEffectiveSegment(state, props),
    base: getBasePositionSetWithSegment(state, props),
    baseSignature: getBaseSignature(state, props),
    classifications: getClassifications(state, props),
    selectedBasket: getSelectedBasket(state, props),
    contributorsGrouping: props.contributorsGrouping,
    expand: getIsExpanded(state, props),
  }),
  required: ["portfolioId", "account"],
  data: [{
    resource: "concentrationContributors",
    as: null,
    url: ({ portfolioId, modelId, selectedDate, expand, segment, classifications }) => {
      if (!selectedDate) { return null }

      return {
        id: `concentrationContributors-${modelId}${portfolioId}${selectedDate}${expand}${JSON.stringify(segment)}`,
        query: `query ($modelId: String!, $portfolioId: String!, $date: Date!, $expand: Boolean, $segment: Segment) {
            model (id: $modelId) {
              simulation (positionSet: {
                type: PORTFOLIO,
                id: $portfolioId,
                segment: $segment,
                expand: $expand
              }, from: $date, to: $date, interval: END_DATE) {
                composition(scaleFormat: PERCENT_EQUITY_GMV) {
                  date
                  gmv
                  referenceEquity
                  positionsCount
                  positions {
                    id
                    bloombergTicker
                    economicExposure
                    country
                    description
                    isUnmodeled
                    ${generateClassificationQueryForContributors("GICS", classifications)}
                    currency
                    assetClass
                    assetSubclass
                    modelProviderId
                  }
                }
              }
            }
          }`,
        variables: {
          modelId,
          portfolioId,
          date: selectedDate,
          segment,
          expand,
        },
      }
    },
  }, {
    resource: "groupedConcentrationContributors",
    as: null,
    url: ({ portfolioId, modelId, selectedDate, expand, contributorsGrouping, segment, classifications }) => {
      if (!contributorsGrouping) { return null }

      const queryGroupBy = getQueryGroupBy(contributorsGrouping)

      return {
        id: `groupedConcentrationContributors-${modelId}${portfolioId}${selectedDate}${expand}${JSON.stringify(queryGroupBy)}${JSON.stringify(segment)}`,
        query: `query ($modelId: String!, $portfolioId: String!, $date: Date!, $expand: Boolean, $groupBy: ContributorGroupType!, $groupById: String, $tier: String, $segment: Segment) {
            model (id: $modelId) {
              simulation (positionSet: {
                type: PORTFOLIO,
                id: $portfolioId,
                segment: $segment,
                expand: $expand
              }, from: $date, to: $date, interval: END_DATE) {
                composition(scaleFormat: PERCENT_EQUITY_GMV) {
                  compositionBy (groupBy: $groupBy, groupById: $groupById, classificationTier: $tier) {
                    id
                    description
                    totalPercentEquity:economicExposure
                    contributors {
                      id
                      economicExposure
                      assetClass
                      assetSubclass
                      country
                      currency
                      description
                      isUnmodeled
                      ${generateClassificationQueryForContributors("GICS", classifications)}
                      bloombergTicker
                      modelProviderId
                    }
                  }
                }
              }
            }
          }`,
        variables: {
          modelId,
          portfolioId,
          date: selectedDate,
          segment,
          expand,
          ...queryGroupBy,
        },
      }
    },
  }, {
    resource: "basketContributors",
    as: null,
    url: ({ modelId, selectedDate, selectedBasket, classifications }) => {
      if (!selectedBasket) return null
      const basketId = sanitizeBasketTicker(get(selectedBasket, "ticker.id"))
      return {
        id: `basketContributors-${modelId}${basketId}${selectedDate}`,
        query: `query ($modelId: String!, $positionSet: PositionSetInput!, $date: Date!) {
          model (id: $modelId) {
            simulation (positionSet: $positionSet, from: $date, to: $date) {
              composition(scaleFormat: PERCENT_EQUITY_GMV) {
                date
                gmv
                referenceEquity
                positionsCount
                positions {
                  id
                  economicExposure
                  country
                  description
                  isUnmodeled
                  ${generateClassificationQueryForContributors("GICS", classifications)}
                  currency
                  assetClass
                  bloombergTicker
                  modelProviderId
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          date: selectedDate,
          positionSet: {
            type: "SWAP",
            id: basketId,
          },
        },
      }
    },
  }, {
    resource: "groupedBasketContributors",
    as: null,
    url: ({ modelId, selectedDate, selectedBasket, contributorsGrouping, classifications }) => {
      if (!selectedBasket || !contributorsGrouping) { return null }

      const { groupBy, tier, groupById } = getQueryGroupBy(contributorsGrouping)

      return {
        id: `groupedBasketContributors-${modelId}${selectedBasket.ticker.id}${selectedDate}${groupBy}${groupById}${tier}`,
        query: `query ($modelId: String!, $positionSet: PositionSetInput!, $date: Date!, $groupBy: ContributorGroupType!, $groupById: String, $tier: String) {
            model (id: $modelId) {
              simulation (positionSet: $positionSet, from: $date, to: $date) {
                composition(scaleFormat: PERCENT_EQUITY_GMV) {
                  compositionBy (groupBy: $groupBy, classificationTier: $tier, groupById: $groupById) {
                    id
                    description
                    totalPercentEquity:economicExposure
                    contributors {
                      id
                      economicExposure
                      assetClass
                      assetSubclass
                      country
                      currency
                      description
                      isUnmodeled
                      ${generateClassificationQueryForContributors("GICS", classifications)}
                      bloombergTicker
                      modelProviderId
                    }
                  }
                }
              }
            }
          }`,
        variables: {
          modelId,
          date: selectedDate,
          positionSet: {
            type: "SWAP",
            id: selectedBasket.ticker.id,
          },
          groupBy,
          groupById,
          tier,
        },
      }
    },
  }],
  mapStateToProps: (state, props) => ({
    ...state.modules.concentrationContributors,
    data: getSortedContributors(state, props),
    groupedData: getSortedGroupContributors(state, props),
    availableRollups: getAvailableRollups(state, props),
    contributorsSummaryText: getContributorsSummaryText(state, props),
    csvData: getCsvData(state, props),
    selectedGroupValue: getSelectedGroupValue(state, props),
    selectedGroup: getSelectedGroup(state, props),
    isOpPro: getIsOpPro(state, props),
    refEquityOverride: hasReferenceEquity(state, props),
    selectedGroupSiblings: getSelectedGroupSiblings(state, props),
    groupsDropdownColumns: getGroupsDropdownColumns(state, props),
  }),
  actions: {
    ...AnalyzeConcentrationDetailsCreators,
    openSecurityProfile: ProfileCreators.openSecurityProfile,
    openFactorProfile: ProfileCreators.openFactorProfile,
    openCompositeSecurity: CompositeCreators.openCompositeSecurity,
  },
})
